/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useImpostazioni } from '../hooks/useImpostazioni';
import { titolare, emailPrivacy, attivita, anniConservazione, inItalia, aggiornataIl } from '../utils/titolare';

const ACCENT = '#3B82F6';

export default function Privacy() {
  const impostazioni = useImpostazioni();
  const email = emailPrivacy(impostazioni);
  const cosaFa = attivita(impostazioni);
  const anni = anniConservazione(impostazioni);
  const italia = inItalia(impostazioni);
  const aggiornata = aggiornataIl(impostazioni);
  return (
    <div style={styles.wrapper}>
      <div style={styles.container}>
        <Link to="/login" style={styles.backLink}><ArrowLeft size={15} /> Torna al login</Link>

        <h1 style={styles.title}>Informativa sul trattamento dei dati personali</h1>
        <p style={styles.updated}>
          Ai sensi degli articoli 13 e 14 del Regolamento (UE) 2016/679 (GDPR)
          {aggiornata && ` · aggiornata al ${aggiornata}`}
        </p>

        <Sezione titolo="1. Titolare del trattamento">
          <p>{titolare(impostazioni)}</p>
          {email && <p>Contatto per questioni relative alla privacy: <a href={`mailto:${email}`} style={styles.inlineLink}>{email}</a>.</p>}
          {impostazioni.privacy_dpo && <p>Responsabile della protezione dei dati (DPO): {impostazioni.privacy_dpo}.</p>}
        </Sezione>

        <Sezione titolo="2. Dati raccolti">
          <p>Nell'ambito dell'utilizzo dell'app raccogliamo:</p>
          <ul style={styles.list}>
            <li>Dati anagrafici e di contatto: nome, cognome o ragione sociale, email, telefono, indirizzo.</li>
            <li>Dati fiscali (per la fatturazione): codice fiscale oppure partita IVA, codice destinatario SDI, PEC, IBAN (per clienti aziendali).</li>
            <li>Dati relativi agli interventi richiesti: indirizzo dell'intervento, descrizione del problema, foto e documenti caricati, note del personale.</li>
            <li>Dati tecnici di utilizzo del servizio: data e ora di accesso, dispositivo utilizzato.</li>
          </ul>
        </Sezione>

        <Sezione titolo="3. Finalità del trattamento">
          <ul style={styles.list}>
            <li>Erogazione del servizio di {cosaFa} richiesto (gestione interventi, appuntamenti, materiali utilizzati).</li>
            <li>Gestione amministrativa e contabile, fatturazione.</li>
            <li>Adempimento di obblighi di legge (es. fiscali, contabili).</li>
            <li>Comunicazioni relative allo stato degli interventi (notifiche in-app).</li>
          </ul>
        </Sezione>

        <Sezione titolo="4. Base giuridica">
          <p>Il trattamento si basa sull'esecuzione di un contratto o di misure precontrattuali richieste dall'interessato (art. 6.1.b GDPR) e, per i dati fiscali, sull'adempimento di obblighi legali (art. 6.1.c GDPR).</p>
        </Sezione>

        <Sezione titolo="5. Natura del conferimento">
          <p>Il conferimento dei dati anagrafici e di contatto è necessario per erogare il servizio richiesto: in mancanza non è possibile dare seguito alla richiesta di intervento. Il conferimento dei dati fiscali è obbligatorio per legge ai fini della fatturazione. Il caricamento di foto e documenti è invece facoltativo e serve solo a descrivere meglio il problema.</p>
        </Sezione>

        <Sezione titolo="6. Conservazione dei dati">
          <p>
            I dati sono conservati per il tempo necessario a erogare il servizio richiesto e, successivamente,
            per il periodo previsto dalla normativa fiscale e contabile applicabile
            {italia
              ? <> (in Italia, tipicamente {anni} anni per la documentazione fiscale)</>
              : <> ({anni} anni per la documentazione fiscale)</>}.
          </p>
        </Sezione>

        <Sezione titolo="7. Destinatari dei dati">
          <p>I dati possono essere trattati dal personale autorizzato di {impostazioni.azienda_denominazione || impostazioni.app_nome} e da eventuali fornitori tecnici (es. hosting, invio email) che agiscono in qualità di responsabili del trattamento ai sensi dell'art. 28 GDPR.</p>
        </Sezione>

        <Sezione titolo="8. Trasferimento dei dati">
          <p>{impostazioni.privacy_trasferimenti}</p>
        </Sezione>

        <Sezione titolo="9. Processo decisionale automatizzato">
          <p>Non vengono effettuati trattamenti automatizzati che producano effetti giuridici o incidano in modo analogamente significativo sull'interessato, né attività di profilazione.</p>
        </Sezione>

        <Sezione titolo="10. Diritti dell'interessato">
          <p>
            In qualsiasi momento è possibile esercitare i diritti previsti dagli articoli 15-22 del GDPR:
            accesso ai dati, rettifica, cancellazione, limitazione del trattamento, portabilità, opposizione.
            È inoltre possibile proporre reclamo
            {italia ? ' al Garante per la protezione dei dati personali' : ' all\'autorità di controllo competente'}.
          </p>
          <p>Per esercitare i tuoi diritti sui dati, se sei già cliente registrato, vai alla sezione <Link to="/account" style={styles.inlineLink}>Il mio account</Link> dell'app, dove puoi scaricare i tuoi dati o richiedere la cancellazione dell'account. {email && <>In alternativa scrivi a <a href={`mailto:${email}`} style={styles.inlineLink}>{email}</a>.</>}</p>
        </Sezione>
      </div>
    </div>
  );
}

function Sezione({ titolo, children }) {
  return (
    <section style={styles.section}>
      <h2 style={styles.sectionTitle}>{titolo}</h2>
      {children}
    </section>
  );
}

const styles = {
  wrapper: { minHeight: '100vh', backgroundColor: '#F9FAFB', padding: '2rem 1rem' },
  container: { maxWidth: '720px', margin: '0 auto', backgroundColor: 'white', borderRadius: '12px', padding: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  backLink: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: ACCENT, textDecoration: 'none', fontSize: '0.88rem', fontWeight: 600, marginBottom: '1.25rem' },
  title: { fontSize: '1.5rem', fontWeight: 700, margin: '0 0 0.3rem', color: '#111827' },
  updated: { color: '#6B7280', fontSize: '0.85rem', margin: '0 0 1.75rem' },
  section: { marginBottom: '1.5rem' },
  sectionTitle: { fontSize: '1.05rem', fontWeight: 700, color: '#111827', margin: '0 0 0.5rem' },
  list: { margin: '0.4rem 0', paddingLeft: '1.3rem', display: 'flex', flexDirection: 'column', gap: '0.3rem', color: '#374151', fontSize: '0.92rem', lineHeight: 1.5 },
  inlineLink: { color: ACCENT, fontWeight: 600 },
};
